import React, { useMemo } from 'react';
import {
  Link as RouterLink,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Link,
  Toolbar,
  Typography,
} from '@mui/material';
import { logout } from '../endpoints/login';
import {
  getDefaultRoute,
  hasPersona,
  PERSONA_APPLICANT,
  PERSONA_LOAN_HOLDER,
} from '../utils/personas';

type PortalNavItem = {
  path: string;
  label: string;
  matchPrefix: boolean;
  /** If set, nav item only shows when user has this persona. */
  requiresPersona?: string;
};

const PORTAL_NAV: PortalNavItem[] = [
  {
    path: '/portal/applications',
    label: 'Applications',
    matchPrefix: true,
    requiresPersona: PERSONA_APPLICANT,
  },
  {
    path: '/portal/loans',
    label: 'Loans',
    matchPrefix: true,
    requiresPersona: PERSONA_LOAN_HOLDER,
  },
  { path: '/portal/account', label: 'Account', matchPrefix: false },
];

function handleLogOut(): void {
  const email = localStorage.getItem('email');
  logout()
    .then(() => {
      localStorage.clear();
      if (email) localStorage.setItem('email', email);
      window.location.href = '/login';
    })
    .catch((error) => {
      console.error('Logout error:', error);
      localStorage.clear();
      if (email) localStorage.setItem('email', email);
      window.location.href = '/login';
    });
}

function navActive(
  pathname: string,
  path: string,
  matchPrefix: boolean
): boolean {
  if (matchPrefix) {
    return pathname === path || pathname.startsWith(`${path}/`);
  }
  return pathname === path;
}

function pathRequiresPersona(
  pathname: string,
  navItems: PortalNavItem[]
): string | null {
  for (const item of navItems) {
    if (!item.requiresPersona) continue;
    if (navActive(pathname, item.path, item.matchPrefix)) {
      return item.requiresPersona;
    }
  }
  return null;
}

/** Portal chrome for loan/applicant/account users (no membership drawer). */
const PortalLayout: React.FC = () => {
  const location = useLocation();
  const firstName = localStorage.getItem('first_name') || 'Guest';
  const personas = localStorage.getItem('personas');

  const visibleNav = useMemo(
    () =>
      PORTAL_NAV.filter(
        (item) =>
          !item.requiresPersona || hasPersona(personas, item.requiresPersona)
      ),
    [personas]
  );

  const required = pathRequiresPersona(location.pathname, PORTAL_NAV);
  if (required && !hasPersona(personas, required)) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  return (
    <Box sx={{ minHeight: '60vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 0, mr: 2, fontWeight: 700, letterSpacing: 0.2 }}
          >
            P.E.O. Portal
          </Typography>
          {visibleNav.map((item) => {
            const active = navActive(
              location.pathname,
              item.path,
              item.matchPrefix
            );
            return (
              <Link
                key={item.path}
                component={RouterLink}
                to={item.path}
                underline="none"
                color={active ? 'primary' : 'text.secondary'}
                sx={{
                  fontWeight: active ? 700 : 500,
                  mr: 1,
                }}
              >
                {item.label}
              </Link>
            );
          })}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {firstName}
          </Typography>
          <Button size="small" onClick={handleLogOut}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Outlet />
      </Container>
    </Box>
  );
};

export default PortalLayout;
